import { DirectorySchemaData } from 'api/types/data/directory/directory';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';

import {
  DatePickerFieldConfig,
  SelectFieldConfig,
  InputFieldConfig,
  FieldConfig,
  DecimalFieldConfig,
  DirectoryFieldConfig,
} from '../types';

interface Props {
  currentSchema?: DirectorySchemaData;
  allSchemas?: DirectorySchemaData[];
}

export const useFieldsConfig = ({
  currentSchema,
  allSchemas,
}: Props): FieldConfig[] => {
  const { language } = useTranslation();

  if (!currentSchema || !allSchemas) {
    return [];
  }

  return currentSchema.fieldDefinitions.flatMap((currentField) => {
    let inputFieldConfig: InputFieldConfig = {};
    let decimalFieldConfig: DecimalFieldConfig = {};
    let datePickerFieldConfig: DatePickerFieldConfig = {};
    let selectFieldConfig: SelectFieldConfig | undefined;
    let directoryFieldConfig: DirectoryFieldConfig | undefined;

    const { validation } = currentField;
    let { component }: { component: FieldConfig['component'] } = currentField;

    switch (currentField.component) {
      case 'Input':
        inputFieldConfig = {
          maxLength:
            validation?.number?.length?.max ??
            validation?.string?.length?.max ??
            undefined,
          minLength:
            validation?.number?.length?.min ??
            validation?.string?.length?.min ??
            undefined,
          minNumber: validation?.number?.min ?? undefined,
          maxNumber: validation?.number?.max ?? undefined,
          isNumber: !!validation?.number,
        };
        break;

      case 'Decimal':
        decimalFieldConfig = {
          maxFloatingPoints: validation?.decimal?.scale ?? undefined,
          maxIntegerLength: validation?.decimal?.max ?? undefined,
        };
        break;

      case 'Datetime':
        datePickerFieldConfig = {
          minDate: validation?.date?.min ? validation.date.min : undefined,
          maxDate: validation?.date?.max ? validation.date.max : undefined,
        };
        break;

      case 'Select':
        let options: { label: string; value: string | number | boolean }[] = [];

        if (currentField.selectOptions) {
          options = lodash.compact(
            currentField.selectOptions.map((option) => {
              if (
                !option.label ||
                option.value === undefined ||
                option.value === null
              ) {
                return null;
              }
              return {
                label:
                  typeof option.label === 'object'
                    ? option.label[language] || option.label.ru
                    : option.label,
                value: option.value,
              };
            })
          );

          selectFieldConfig = {
            options,
            optionLabelKey: 'label',
            optionValueKey: 'value',
          };
        } else if (currentField.reference) {
          component = 'Directory';
          const referenceSchemaName =
            currentField.reference.directorySchemaName;

          const referenceFieldName = currentField.reference.fieldName;

          directoryFieldConfig = {
            directorySchemaName: referenceSchemaName,
            fieldName: referenceFieldName,
          };
        }

        break;
      default:
        break;
    }

    const commonConfig = {
      component,
      label: currentField.label[language] || currentField.label.ru,
      placeholder: currentField.placeholder
        ? currentField.placeholder[language] || currentField.placeholder.ru
        : undefined,
      inputFieldConfig,
      decimalFieldConfig,
      datePickerFieldConfig,
      selectFieldConfig,
      directoryFieldConfig,
      isMultiLanguage: currentField.isMultiLanguage,
      isRequired: currentField.isRequired,
    };

    if (currentField.isMultiLanguage) {
      return [
        {
          name: `${currentField.name}.ru`,
          ...commonConfig,
          label: `${commonConfig.label} (RU)`,
        },
        {
          name: `${currentField.name}.en`,
          ...commonConfig,
          label: `${commonConfig.label} (EN)`,
        },
      ];
    }

    return [
      {
        name: currentField.name,
        ...commonConfig,
      },
    ];
  });
};
